import { useCallback, useState } from "react"

import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGlTransactionFileUploadReturn {
  uploadFilesForTransactions: (files: File[], transactionIds: string[]) => Promise<void>
  isUploading: boolean
}

export const useGlTransactionFileUpload = (): UseGlTransactionFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false)
  const { uploadFile } = useFileUpload()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const uploadFilesForTransactions = useCallback(
    async (files: File[], transactionIds: string[]) => {
      if (!files.length || !transactionIds.length) return

      setIsUploading(true)
      try {
        for (const transactionId of transactionIds) {
          for (const file of files) {
            const success = await uploadFile({
              file,
              type: FieldGroupEnum.Miscellaneous,
              metadata: {
                referenceId: transactionId,
                referenceTableName: ReferenceTableName.GlTransaction,
                fieldGroup: FieldGroupEnum.Miscellaneous,
              },
              label: file.name,
            })

            if (!success) {
              notify({
                type: NotificationType.Error,
                silent: false,
                message: `Failed to upload ${file.name}. Please try again.`,
              })
            }
          }
        }
      } finally {
        setIsUploading(false)
      }
    },
    [uploadFile, notify]
  )

  return { uploadFilesForTransactions, isUploading }
}
